import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import { fileLinkClick } from '../utils/openFile.js';

// Klussenbord: jobs the club needs help with, and who offered. The public page
// (<club>/vrijwilligers) shows the open ones to anyone, without logging in.

const ERRORS = {
  title_required: 'Geef de klus een titel.', invalid_start: 'De starttijd klopt niet.', invalid_end: 'De eindtijd klopt niet.',
  end_before_start: 'De eindtijd moet na de starttijd liggen.', invalid_spots: 'Aantal plekken: een getal van 1 tot 500 (of leeg voor onbeperkt).',
  invalid_section: 'Kies een sectie van deze club.', invalid_commission: 'Kies een commissie van deze club.',
  invalid_coordinator: 'De coördinator moet een actief lid zijn.', has_signups: 'Er zijn al aanmeldingen; sluit de klus in plaats van hem te verwijderen.',
  already_handled: 'Deze aanmelding is al afgehandeld.', invalid_template: 'Kies een sjabloon van deze club.',
  template_not_found: 'Dat sjabloon bestaat niet (meer).',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';

const STATUS_LABELS = { concept: 'Concept', open: 'Open', gesloten: 'Gesloten' };
const SIGNUP_LABELS = { nieuw: 'Nieuw', goedgekeurd: 'Goedgekeurd', afgewezen: 'Afgewezen' };

function formatWhen(o) {
  if (o.kind === 'vacature') return o.commitment || 'Vaste rol';
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(o.starts_at || '');
  if (!m) return 'Geen vaste datum';
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const date = d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
  const end = o.ends_at ? `–${o.ends_at.slice(11, 16)}` : '';
  return `${date} ${m[4]}:${m[5]}${end}`;
}

function spotsText(o) {
  if (o.spots == null) return `${o.approved_count} goedgekeurd · ${o.pending_count} nieuw`;
  if (o.kind === 'vacature') return `${o.approved_count}/${o.spots} gevonden · ${o.pending_count} nieuw`;
  return `${o.approved_count}/${o.spots} goedgekeurd · ${o.pending_count} nieuw · nog ${o.spots_left} ${o.spots_left === 1 ? 'plek' : 'plekken'} vrij`;
}

const EMPTY = { title: '', description: '', starts_at: '', ends_at: '', location: '', spots: '', section_id: '', commission_id: '', coordinator_id: '', status: 'open', commitment: '', requirements: '', onboarding_template_id: '' };

function OpeningForm({ initial, kind, sections, members, templates, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, kind, ...initial });
  const isVacancy = form.kind === 'vacature';
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const commissions = (sections.find(s => String(s.id) === String(form.section_id))?.commissions) || [];

  async function save() {
    setSaving(true); setError(null);
    try {
      const body = JSON.stringify({ ...form, spots: form.spots === '' ? null : Number(form.spots) });
      const saved = initial?.id
        ? await api(`/api/volunteers/openings/${initial.id}`, { method: 'PUT', body })
        : await api('/api/volunteers/openings', { method: 'POST', body });
      onSaved(saved);
    } catch (err) { setError(message(err)); }
    setSaving(false);
  }

  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <label class="form-label">Titel
        <input class="form-input" value={form.title} maxLength={200} onInput={e => set('title', e.target.value)} placeholder={isVacancy ? 'Jeugdtrainer O11' : 'Bardienst jeugdtoernooi'} />
      </label>
      <label class="form-label">Omschrijving
        <textarea class="form-input" rows={3} value={form.description || ''} maxLength={4000} onInput={e => set('description', e.target.value)} placeholder="Wat moet er gebeuren, wat moet iemand weten?" />
      </label>
      <AiAssist kind="volunteer_description" value={form.description || ''} context={{ title: form.title, kind: isVacancy ? 'vacature' : 'klus' }} onUse={text => set('description', text)} />
      {isVacancy ? (
        <div style="display:flex;flex-direction:column;gap:10px">
          <label class="form-label">Tijdsinvestering
            <input class="form-input" value={form.commitment || ''} maxLength={200} onInput={e => set('commitment', e.target.value)} placeholder="2 trainingen per week + wedstrijd op zaterdag" />
          </label>
          <label class="form-label">Wat vragen we
            <textarea class="form-input" rows={2} value={form.requirements || ''} maxLength={1000} onInput={e => set('requirements', e.target.value)} placeholder="VOG; ervaring met jeugd is fijn maar niet nodig" />
          </label>
          <AiAssist kind="volunteer_requirements" value={form.requirements || ''} context={{ title: form.title }} onUse={text => set('requirements', text)} />
        </div>
      ) : (
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
          <label class="form-label">Start
            <input class="form-input" type="datetime-local" value={form.starts_at || ''} onInput={e => set('starts_at', e.target.value)} />
          </label>
          <label class="form-label">Einde
            <input class="form-input" type="datetime-local" value={form.ends_at || ''} onInput={e => set('ends_at', e.target.value)} />
          </label>
        </div>
      )}
      <div style="display:grid;grid-template-columns:2fr 1fr;gap:8px">
        <label class="form-label">Locatie
          <input class="form-input" value={form.location || ''} maxLength={200} onInput={e => set('location', e.target.value)} placeholder="Kantine" />
        </label>
        <label class="form-label">{isVacancy ? 'Aantal mensen' : 'Plekken'}
          <input class="form-input" type="number" min="1" max="500" value={form.spots ?? ''} onInput={e => set('spots', e.target.value)} placeholder="onbeperkt" />
        </label>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <label class="form-label">Sectie
          <select class="form-input" value={form.section_id || ''} onChange={e => { set('section_id', e.target.value); set('commission_id', ''); }}>
            <option value="">—</option>
            {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label class="form-label">Commissie
          <select class="form-input" value={form.commission_id || ''} onChange={e => set('commission_id', e.target.value)} disabled={!commissions.length}>
            <option value="">—</option>
            {commissions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <label class="form-label">Coördinator
          <select class="form-input" value={form.coordinator_id || ''} onChange={e => set('coordinator_id', e.target.value)}>
            <option value="">{initial?.id ? '— ongewijzigd —' : 'Ikzelf'}</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
          </select>
        </label>
        <label class="form-label">Status
          <select class="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="open">Open (zichtbaar op de openbare pagina)</option>
            <option value="concept">Concept (nog niet zichtbaar)</option>
            <option value="gesloten">Gesloten</option>
          </select>
        </label>
      </div>
      {isVacancy && (
        <label class="form-label">Inwerken (standaard bij goedkeuren)
          <select class="form-input" value={form.onboarding_template_id || ''} onChange={e => set('onboarding_template_id', e.target.value)}>
            <option value="">Geen inwerkproces</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}
      {error && <div class="alert alert-error">{error}</div>}
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button>
      </div>
    </div>
  );
}

function Signups({ opening, canUpdate, templates, onChanged }) {
  const [templateId, setTemplateId] = useState(opening.onboarding_template_id ? String(opening.onboarding_template_id) : '');
  const [signups, setSignups] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api(`/api/volunteers/openings/${opening.id}/signups`).then(setSignups).catch(err => setError(message(err)));
  useEffect(load, [opening.id]);

  async function decide(signup, decision) {
    if (busy) return;
    setBusy(signup.id); setError(null);
    const body = decision === 'approve' && opening.kind === 'vacature' && templateId ? JSON.stringify({ template_id: Number(templateId) }) : undefined;
    try { await api(`/api/volunteers/signups/${signup.id}/${decision}`, { method: 'POST', body }); await load(); onChanged(); }
    catch (err) { setError(message(err)); }
    setBusy(null);
  }

  if (!signups) return <div class="text-muted text-sm">{error || 'Laden…'}</div>;
  if (!signups.length) return <div class="text-muted text-sm">Nog geen aanmeldingen.</div>;
  return (
    <div style="display:flex;flex-direction:column;gap:6px">
      {error && <div class="alert alert-error">{error}</div>}
      {canUpdate && opening.kind === 'vacature' && signups.some(s => s.status === 'nieuw') && (
        <label class="text-sm" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0">
          Bij goedkeuren inwerkproces starten:
          <select class="form-input" style="flex:1;min-width:0;max-width:100%;height:30px;font-size:12px" value={templateId} onChange={e => setTemplateId(e.target.value)}>
            <option value="">Geen</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}
      {signups.map(s => (
        <div key={s.id} style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--color-border);border-radius:8px;flex-wrap:wrap">
          <div style="min-width:0">
            <div style="font-weight:500;font-size:14px">{s.name}{s.user_id ? ' · lid met account' : ''}</div>
            <div class="text-sm text-muted">{[s.email, s.phone].filter(Boolean).join(' · ')}</div>
            {s.message && <div class="text-sm" style="margin-top:2px">“{s.message}”</div>}
            <div class="text-sm text-muted">{SIGNUP_LABELS[s.status]} · {parseServerDate(s.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}{s.task_id ? ' · taak aangemaakt' : ''}{s.process_title ? ` · proces: ${s.process_title}` : ''}</div>
          </div>
          {canUpdate && s.status === 'nieuw' && (
            <div style="display:flex;gap:6px">
              <Button size="sm" disabled={busy === s.id} onClick={() => decide(s, 'approve')}>Goedkeuren</Button>
              <Button size="sm" variant="secondary" disabled={busy === s.id} onClick={() => decide(s, 'reject')}>Afwijzen</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function VolunteersView() {
  const auth = useAuth();
  const [access, setAccess] = useState(null);
  const [openings, setOpenings] = useState([]);
  const [sections, setSections] = useState([]);
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState('klus');
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [error, setError] = useState(null);

  const club = (auth.clubs || []).find(c => Number(c.id) === Number(auth.activeClubId));
  const publicUrl = club?.slug ? `${location.origin}/${club.slug}/vrijwilligers` : null;
  const load = () => api('/api/volunteers/openings').then(setOpenings).catch(err => setError(message(err)));

  useEffect(() => {
    api('/api/volunteers/access').then(a => {
      setAccess(a);
      if (!a.read) return;
      load();
      api('/api/settings/sections').then(s => setSections(Array.isArray(s) ? s : s.sections || [])).catch(() => {});
      api('/api/users/members').then(m => setMembers(Array.isArray(m) ? m : [])).catch(() => {});
      api('/api/volunteers/templates').then(setTemplates).catch(() => {});
    }).catch(() => setAccess({ read: false }));
  }, [auth.activeClubId]);

  async function remove(o) {
    if (!confirm(`${o.kind === 'vacature' ? 'Vacature' : 'Klus'} "${o.title}" verwijderen?`)) return;
    try { await api(`/api/volunteers/openings/${o.id}`, { method: 'DELETE' }); load(); }
    catch (err) { setError(message(err)); }
  }

  // For the club's own website: the page in a frame that grows with its content
  const embedCode = publicUrl && [
    `<iframe id="vereniging4u-klussenbord" src="${publicUrl}" title="Klussenbord" loading="lazy" style="width:100%;border:0;min-height:320px"></iframe>`,
    `<script>window.addEventListener('message',function(e){if(e.origin==='${location.origin}'&&e.data&&e.data.type==='vereniging4u-height'){document.getElementById('vereniging4u-klussenbord').style.height=e.data.height+'px';}});</script>`,
  ].join('\n');

  async function copy(text, what) {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(null), 2000); } catch { /* shown as text anyway */ }
  }

  if (!access) return <div class="text-center text-muted" style="padding:40px">Laden…</div>;
  if (!access.read) return <div class="text-center text-muted" style="padding:40px">Je hebt geen toegang tot het klussenbord. Vraag een beheerder om het recht ‘Vrijwilligers’.</div>;

  return (
    <div>
      <div class="page-header" style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
        <h1 class="page-title">Klussenbord</h1>
        {access.create && <Button onClick={() => setEditing({ kind: tab })}>{tab === 'vacature' ? '+ Nieuwe vacature' : '+ Nieuwe klus'}</Button>}
      </div>

      <div style="display:flex;gap:6px;margin-bottom:12px">
        {[['klus', 'Klussen'], ['vacature', 'Vacatures']].map(([k, label]) => (
          <Button key={k} size="sm" variant={tab === k ? 'primary' : 'secondary'} onClick={() => { setTab(k); setExpanded(null); }}>
            {label} ({openings.filter(o => o.kind === k).length})
          </Button>
        ))}
      </div>

      {publicUrl && (
        <Card>
          <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
            <div style="min-width:0">
              <div style="font-weight:500">Openbare pagina</div>
              <div class="text-sm text-muted">Deel deze link in de app, een nieuwsbrief of via een QR-code. Iedereen kan zich aanmelden, ook zonder account.</div>
              <a href={`${publicUrl}?from=app`} target="_blank" rel="noopener" class="text-sm" style="word-break:break-all">{publicUrl}</a>
            </div>
            <div style="display:flex;gap:6px;flex-wrap:wrap">
              <Button variant="secondary" size="sm" onClick={() => copy(publicUrl, 'link')}>{copied === 'link' ? 'Gekopieerd' : 'Link kopiëren'}</Button>
              <Button variant="secondary" size="sm" onClick={() => setShowShare(v => !v)}>{showShare ? 'Verberg QR en insluiten' : 'QR-code en insluiten'}</Button>
            </div>
          </div>
          {showShare && (
            <div style="display:flex;gap:16px;flex-wrap:wrap;margin-top:12px;align-items:flex-start">
              <div style="text-align:center">
                <img src="/api/volunteers/qr.svg" alt="QR-code naar de openbare pagina" width="140" height="140" style="background:#fff;border-radius:8px;border:1px solid var(--color-border)" />
                <div><a href="/api/volunteers/qr.svg" download={`klussenbord-${club.slug}.svg`} data-native onClick={e => fileLinkClick(e, `klussenbord-${club.slug}.svg`)} class="text-sm">QR-code downloaden</a></div>
              </div>
              <div style="flex:1;min-width:220px">
                <div class="text-sm" style="font-weight:500">Op de website van de club</div>
                <div class="text-sm text-muted">Geef deze code aan de webbeheerder; die plakt hem op bijvoorbeeld een pagina “Vrijwilligers”. Het klussenbord verschijnt dan daar, en aanmeldingen komen gewoon hier binnen.</div>
                <textarea class="form-input" readOnly rows={4} style="font-family:monospace;font-size:11px;margin-top:6px" value={embedCode} onFocus={e => e.target.select()} />
                <Button variant="secondary" size="sm" onClick={() => copy(embedCode, 'embed')}>{copied === 'embed' ? 'Gekopieerd' : 'Insluitcode kopiëren'}</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {error && <div class="alert alert-error" style="margin:12px 0">{error}</div>}

      <div style="display:flex;flex-direction:column;gap:10px;margin-top:12px">
        {!openings.some(o => o.kind === tab) && <div class="text-center text-muted" style="padding:32px">{tab === 'vacature' ? 'Nog geen vacatures. Zet een vaste rol uit en deel de link.' : 'Nog geen klussen. Maak er een aan en deel de link.'}</div>}
        {openings.filter(o => o.kind === tab).map(o => (
          <Card key={o.id}>
            <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap">
              <div style="min-width:0">
                <div style="font-weight:600">{o.title} <span class="text-sm text-muted">· {STATUS_LABELS[o.status]}</span></div>
                <div class="text-sm text-muted">{[formatWhen(o), o.location, o.section_name, o.commission_name].filter(Boolean).join(' · ')}</div>
                <div class="text-sm">{spotsText(o)}{o.coordinator_name ? ` · coördinator: ${o.coordinator_name}` : ''}</div>
              </div>
              <div style="display:flex;gap:6px;flex-wrap:wrap">
                <Button size="sm" variant="secondary" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                  {expanded === o.id ? 'Verberg aanmeldingen' : `Aanmeldingen (${o.approved_count + o.pending_count})`}
                </Button>
                {(o.can_edit ?? access.update) && <Button size="sm" variant="secondary" onClick={() => setEditing(o)}>Bewerken</Button>}
                {(o.can_edit ?? access.update) && o.status !== 'gesloten' && (
                  <Button size="sm" variant="secondary" onClick={async () => { try { await api(`/api/volunteers/openings/${o.id}`, { method: 'PUT', body: JSON.stringify({ status: 'gesloten' }) }); load(); } catch (err) { setError(message(err)); } }}>Sluiten</Button>
                )}
                {(o.can_delete ?? access.delete) && o.approved_count + o.pending_count === 0 && <Button size="sm" variant="secondary" onClick={() => remove(o)}>Verwijderen</Button>}
              </div>
            </div>
            {o.kind === 'vacature' && o.requirements && <div class="text-sm text-muted" style="margin-top:4px">Wat we vragen: {o.requirements}</div>}
            {expanded === o.id && <div style="margin-top:12px"><Signups opening={o} canUpdate={o.can_decide ?? access.update} templates={templates} onChanged={load} /></div>}
          </Card>
        ))}
      </div>

      <Modal isOpen={!!editing} title={`${(editing?.kind || tab) === 'vacature' ? 'Vacature' : 'Klus'} ${editing?.id ? 'bewerken' : 'toevoegen'}`} onClose={() => setEditing(null)}>
        {editing && <OpeningForm initial={editing} kind={editing.kind || tab} sections={sections} members={members} templates={templates}
          onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}
